import {
  addDays,
  addWeeks,
  endOfISOWeek,
  endOfMonth,
  format,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';
import { dbDayDateToken } from '../../../static/dbDayDateToken';
import { JobTypeEnum } from '../../models/jobTypeEnum';
import { RoleEnum } from '../../models/role.enum';
import { ShiftEnum } from '../../models/shift.enum';
import { User } from '../../models/user';
import { getUtcStartOfDay } from '../removeTimeZoneOffset';
import { MongoSearchQueryParams } from './JobsMongoQueryOptions';

export function getJobFilterParamsMongoQuery(
  queryParams: MongoSearchQueryParams,
  user: User,
): object[] {
  const conditions: object[] = [];

  if (queryParams.createdBy && queryParams.createdBy != 'ALL') {
    const creatorIds = queryParams.createdBy.split(',').filter(Boolean);
    if (creatorIds.length) {
      conditions.push({
        $or: [
          { 'createdBy.userId': { $in: creatorIds } },
          { 'transfers.targetCustomer.editorId': { $in: creatorIds } },
        ],
      });
    }
  }

  if (queryParams.branch && queryParams.branch != 'ALL') {
    conditions.push({
      $or: [
        { branch: queryParams.branch },
        { 'transfers.targetCustomer.branchId': queryParams.branch },
      ],
    });
  }

  if (queryParams.shift && queryParams.shift != 'ALL') {
    conditions.push({
      'shifts.shift': ShiftEnum[queryParams.shift as keyof typeof ShiftEnum],
    });
  }

  if (queryParams.certificates) {
    const certificates = queryParams.certificates.split(',');
    conditions.push({
      'certificates.id': { $in: certificates },
    });
  }

  if (queryParams.departments) {
    const departmentIds = queryParams.departments.split(',').filter(Boolean);
    if (departmentIds.length) {
      conditions.push({
        'department.id': { $in: departmentIds },
      });
    }
  }

  if (queryParams.jobType && queryParams.jobType !== 'ALL') {
    const jobTypes = queryParams.jobType.split(',');
    const jobTypeEnums = jobTypes
      .map((jobType) => JobTypeEnum[jobType as keyof typeof JobTypeEnum])
      .filter((jobType) => jobType !== undefined);
    if (jobTypeEnums.length > 0) {
      conditions.push({
        jobType: { $in: jobTypeEnums },
      });
    }
  }

  if (
    queryParams.demandReason &&
    queryParams.demandReason != 'ALL' &&
    user.role === RoleEnum.DISPO &&
    user.customerId
  ) {
    const customerIdKey = `customerInternalInformation.${user.customerId}.demandReason.id`;
    conditions.push({
      [customerIdKey]: queryParams.demandReason,
    });
  }

  if (queryParams.range && queryParams.range != 'ALL') {
    const rangeCondition = getDateRangeCondition(queryParams.range);
    if (rangeCondition) {
      conditions.push(rangeCondition);
    }
  }

  return conditions;
}

function getDateRangeCondition(range: string): object | undefined {
  const splitRange = range.split(',');
  switch (splitRange[0]) {
    case 'TODAY':
      return {
        date: format(getUtcStartOfDay(new Date()), dbDayDateToken),
      };
    case 'TOMORROW':
      return {
        date: format(getUtcStartOfDay(addDays(new Date(), 1)), dbDayDateToken),
      };
    case 'WEEK':
      return {
        date: {
          $gte: format(
            getUtcStartOfDay(startOfISOWeek(new Date())),
            dbDayDateToken,
          ),
          $lte: format(
            getUtcStartOfDay(endOfISOWeek(new Date())),
            dbDayDateToken,
          ),
        },
      };
    case 'NEXT_WEEK':
      return {
        date: {
          $gte: format(
            getUtcStartOfDay(startOfISOWeek(addWeeks(new Date(), 1))),
            dbDayDateToken,
          ),
          $lte: format(
            getUtcStartOfDay(endOfISOWeek(addWeeks(new Date(), 1))),
            dbDayDateToken,
          ),
        },
      };
    case 'MONTH':
      return {
        date: {
          $gte: format(
            getUtcStartOfDay(startOfMonth(new Date())),
            dbDayDateToken,
          ),
          $lte: format(
            getUtcStartOfDay(endOfMonth(new Date())),
            dbDayDateToken,
          ),
        },
      };
    case 'CUSTOM':
      if (splitRange[1] && splitRange[2]) {
        return {
          date: {
            $gte: splitRange[1],
            $lte: splitRange[2],
          },
        };
      }
      return undefined;
    default:
      return undefined;
  }
}
